import React from "react";
import { SubscriptionPlan } from "enums/subscription-plans";
import {
  FreeTierIcon,
  ProTierIcon,
  DeepTierIcon,
  TeamTierIcon,
  EnterpriseTierIcon,
} from "design_system";

// Map of subscription plans to their corresponding icon components
export const subscriptionPlanIcons = {
  [SubscriptionPlan.FREE]: FreeTierIcon,
  [SubscriptionPlan.PREMIUM]: ProTierIcon,
  [SubscriptionPlan.DEEP]: DeepTierIcon,
  [SubscriptionPlan.TEAM]: TeamTierIcon,
  [SubscriptionPlan.ENTERPRISE]: EnterpriseTierIcon,
} as const;

// Map of subscription plans to their user-facing display labels
export const subscriptionPlanLabels = {
  [SubscriptionPlan.FREE]: "Free",
  [SubscriptionPlan.PREMIUM]: "Pro", // Note: PREMIUM enum shows as "Pro" to users
  [SubscriptionPlan.DEEP]: "Deep",
  [SubscriptionPlan.TEAM]: "Team",
  [SubscriptionPlan.ENTERPRISE]: "Enterprise",
} as const;

/**
 * Get the user-facing label for a subscription plan
 * @param plan - The subscription plan enum value
 * @returns The display label for the plan (e.g., "Pro" for PREMIUM)
 */
export function getSubscriptionPlanLabel(plan: SubscriptionPlan): string {
  return subscriptionPlanLabels[plan] || plan;
}

/**
 * Get the icon component for a given subscription plan
 * @param plan - The subscription plan enum value or lowercase string
 * @returns The corresponding icon component
 */
function getSubscriptionPlanIcon(
  plan: SubscriptionPlan | string
): React.FC<{ size?: "sm" | "default" | "lg"; className?: string }> {
  // Handle lowercase string values
  const normalizedPlan =
    typeof plan === "string"
      ? Object.values(SubscriptionPlan).find(
          (p) => p.toLowerCase() === plan.toLowerCase()
        )
      : plan;

  if (!normalizedPlan || !(normalizedPlan in subscriptionPlanIcons)) {
    // Default to Free tier icon if plan not found
    return FreeTierIcon;
  }

  return subscriptionPlanIcons[normalizedPlan as SubscriptionPlan];
}

/**
 * Render a subscription plan icon as JSX
 * @param plan - The subscription plan enum value or lowercase string
 * @param size - Icon size (sm, default, lg)
 * @param className - Additional CSS classes
 * @returns JSX element of the appropriate icon
 */
export function SubscriptionPlanIcon({
  plan,
  size = "default",
  className,
}: {
  plan: SubscriptionPlan | string;
  size?: "sm" | "default" | "lg";
  className?: string;
}) {
  const IconComponent = getSubscriptionPlanIcon(plan);
  return <IconComponent size={size} className={className} />;
}
